import { useEffect, useRef } from "react";

import { computeStamps, getTipCanvas, hashString } from "@excalidraw/element";

import type { BrushParams } from "@excalidraw/element";
import type { LocalPoint } from "@excalidraw/math/types";

const W = 300;
const H = 44;

/** fixed S-curve, pressure swelling mid-stroke — same curve for every brush
 *  so previews are comparable */
const CURVE: LocalPoint[] = Array.from({ length: 48 }, (_, i) => {
  const t = i / 47;
  return [
    8 + t * (W - 16),
    H / 2 + Math.sin(t * Math.PI * 2) * (H / 2 - 9),
  ] as LocalPoint;
});
const PRESSURES = CURVE.map((_, i) => {
  const t = i / (CURVE.length - 1);
  return 0.25 + 0.75 * Math.sin(t * Math.PI);
});

export const BrushPreview = ({
  params,
  size,
  color,
}: {
  params: BrushParams;
  size: number;
  color: string;
}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    ctx.clearRect(0, 0, W, H);
    const previewSize = Math.min(size, H - 6);
    const seed = hashString(`preview`);
    const tip = getTipCanvas(params, previewSize, color, seed);
    if (!tip) {
      return;
    }
    for (const s of computeStamps(
      CURVE,
      PRESSURES,
      params,
      previewSize,
      seed,
    )) {
      ctx.save();
      ctx.globalAlpha = s.alpha;
      ctx.translate(s.x, s.y);
      ctx.rotate(s.rotation);
      ctx.drawImage(tip, -s.size / 2, -s.size / 2, s.size, s.size);
      ctx.restore();
    }
  }, [params, size, color]);
  return <canvas ref={ref} width={W} height={H} className="brush-preview" />;
};
